/**
 * 🎮 スマホ向け固定フッターメニュー
 * パズル＆サバイバル風の5タブ構成 - 真決DAO世界観版
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermission } from '../hooks/usePermission';
import { useNotificationCount } from '../hooks/useNotificationCount';

// safeCall機能を直接埋め込み（Vercel対応）
const isSafeFunction = (fn) => {
  return typeof fn === 'function';
};

const safeCall = (fn, defaultValue = null, ...args) => {
  try {
    if (isSafeFunction(fn)) {
      return fn(...args);
    }
    console.warn('⚠️ 関数ではない値が呼び出されました:', typeof fn);
    return defaultValue;
  } catch (error) {
    console.error('🚨 関数呼び出しエラー:', error);
    return defaultValue;
  }
};

const MobileFooter = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Component rendered

  // 安全なフック呼び出し
  const permissionResult = safeCall(() => usePermission(), {
    hasPermission: () => true,
  });
  const notificationResult = safeCall(() => useNotificationCount(), {
    counts: {},
  });

  const { hasPermission } = permissionResult || { hasPermission: () => true };
  const { counts } = notificationResult || { counts: {} };

  // Hooks initialized

  // フッタータブの定義 - 新仕様「森の箱庭」版
  const footerTabs = safeCall(
    () => [
      {
        id: 'forest',
        icon: '🌱',
        label: '森',
        path: '/dashboard/forest',
        badge: null,
        active:
          location.pathname === '/dashboard/forest' ||
          location.pathname.startsWith('/forest-ecosystem'),
        disabled: !safeCall(() => hasPermission(1), false), // ゲスト以上で閲覧可能
      },
      {
        id: 'projects',
        icon: '🧩',
        label: '案件',
        path: '/dashboard/applications',
        badge: safeCall(
          () => (counts.projects > 0 ? counts.projects : null),
          null
        ),
        active:
          location.pathname === '/dashboard/applications' ||
          location.pathname === '/projects' ||
          location.pathname.startsWith('/projects/') ||
          location.pathname === '/applications' ||
          location.pathname === '/recruitments' ||
          location.pathname === '/store-application-status',
        disabled: !safeCall(() => hasPermission(2), false), // 準会員以上で閲覧可能
      },
      {
        id: 'home',
        icon: '🏠',
        label: 'ホーム',
        path: '/dashboard',
        badge: null,
        active: location.pathname === '/dashboard',
        disabled: false,
      },
      {
        id: 'community',
        icon: '💬',
        label: '交流',
        path: '/dashboard/social',
        badge: safeCall(() => (counts.chat > 0 ? counts.chat : null), null),
        active:
          location.pathname === '/dashboard/social' ||
          location.pathname.startsWith('/social/') ||
          location.pathname.startsWith('/chat/') ||
          location.pathname.startsWith('/friends/'),
        disabled: !safeCall(() => hasPermission(2), false), // 準会員以上で利用可能
      },
      {
        id: 'stores',
        icon: '🏪',
        label: 'ストア',
        path: '/dashboard/store',
        badge: safeCall(() => (counts.stores > 0 ? counts.stores : null), null),
        active: location.pathname === '/dashboard/store',
        disabled: !safeCall(() => hasPermission(1), false), // ゲスト以上で閲覧可能
      },
    ],
    []
  );

  const handleTabClick = (tab) => {
    // 権限不足の場合は何もしない（ボタンが非活性なのでクリックできない）
    if (tab.disabled) {
      return;
    }

    // 安全なnavigate呼び出し
    const navigationResult = safeCall(() => {
      if (isSafeFunction(navigate)) {
        navigate(tab.path);
        return true;
      }
      return false;
    }, false);

    // navigate が失敗した場合のフォールバック
    if (!navigationResult) {
      try {
        window.location.href = tab.path;
      } catch (error) {
        console.error('🚨 フォールバックナビゲーションエラー:', error);
      }
    }
  };

  return (
    <>
      {/* モバイル版：底部フッター */}
      <footer className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="flex items-center justify-around h-16 px-2">
          {footerTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              disabled={tab.disabled}
              className={`
                relative flex flex-col items-center justify-center min-w-0 flex-1 px-1 py-1
                border-0 outline-none focus:outline-none active:outline-none
                transform-none hover:transform-none active:transform-none
                ${
                  tab.active
                    ? 'text-white bg-green-500 rounded-lg pointer-events-none'
                    : tab.disabled
                      ? 'text-gray-300 opacity-50 cursor-not-allowed'
                      : 'text-white bg-blue-900 cursor-pointer rounded-lg'
                }
              `}
            >
              {/* アイコン + テキスト重ね */}
              <div className="relative flex items-center justify-center">
                <span className="text-xl -translate-y-1">{tab.icon}</span>
                <span className="absolute inset-0 flex items-end justify-center text-xs font-bold whitespace-nowrap text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)] translate-y-0.5">
                  {tab.label}
                </span>
                {tab.badge && tab.badge > 0 && (
                  <span
                    className={`
                      absolute -top-1 -right-1 min-w-[16px] h-4 
                      flex items-center justify-center text-[10px] font-bold 
                      text-white bg-red-500 rounded-full
                      ${tab.badge > 99 ? 'px-1' : ''}
                    `}
                  >
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </footer>

      {/* PC版：右サイドウィジェット */}
      <aside className="hidden md:block fixed right-4 top-1/2 -translate-y-1/2 z-50">
        <div className="flex flex-col space-y-1">
          {footerTabs.map((tab) => (
            <button
              key={`desktop-${tab.id}`}
              onClick={() => handleTabClick(tab)}
              disabled={tab.disabled}
              className={`
                relative flex items-center justify-center w-12 h-12 rounded-lg
                transition-all duration-200 ease-in-out
                ${
                  tab.active
                    ? 'text-white bg-green-500/80 shadow-md pointer-events-none'
                    : tab.disabled
                      ? 'text-gray-300 opacity-50 cursor-not-allowed'
                      : 'text-white bg-blue-900/80 hover:bg-blue-800/90 cursor-pointer'
                }
              `}
              title={tab.label}
            >
              {/* アイコン + テキスト重ね */}
              <div className="relative flex items-center justify-center">
                <span className="text-xl -translate-y-1">{tab.icon}</span>
                <span className="absolute inset-0 flex items-end justify-center text-xs font-bold whitespace-nowrap text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)] translate-y-0.5">
                  {tab.label}
                </span>
              </div>
              {tab.badge && tab.badge > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </aside>
    </>
  );
};

export default MobileFooter;
